const HELIUS_RPC_URL = 'https://mainnet.helius-rpc.com';
const MIN_REQUEST_GAP_MS = 100;

let lastRequestTime = 0;

async function waitForRateLimit(): Promise<void> {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < MIN_REQUEST_GAP_MS) {
    await new Promise((resolve) =>
      setTimeout(resolve, MIN_REQUEST_GAP_MS - timeSinceLastRequest)
    );
  }

  lastRequestTime = Date.now();
}

function getApiKey(): string {
  const apiKey = import.meta.env.VITE_HELIUS_API_KEY;
  if (!apiKey) {
    throw new Error('Helius API key not configured. Please add VITE_HELIUS_API_KEY to your .env file.');
  }
  return apiKey;
}

export interface HeliusAssetResponse {
  id: string;
  content: {
    json_uri: string;
    metadata: {
      name: string;
      symbol: string;
      description?: string;
      attributes?: Array<{
        trait_type: string;
        value: string | number;
      }>;
    };
    links?: {
      image?: string;
      external_url?: string;
    };
    files?: Array<{
      uri: string;
      mime: string;
    }>;
  };
  grouping?: Array<{
    group_key: string;
    group_value: string;
    verified?: boolean;
    collection_metadata?: {
      name?: string;
    };
  }>;
  ownership: {
    owner: string;
    delegate?: string;
    delegated: boolean;
    ownership_model: string;
  };
  royalty: {
    royalty_model: string;
    target?: string;
    percent: number;
    basis_points: number;
    primary_sale_happened: boolean;
    locked: boolean;
  };
  creators: Array<{
    address: string;
    share: number;
    verified: boolean;
  }>;
  authorities: Array<{
    address: string;
    scopes: string[];
  }>;
  mutable: boolean;
  burnt: boolean;
  supply?: {
    print_max_supply: number;
    print_current_supply: number;
    edition_nonce?: number;
  };
}

export interface HeliusRpcResponse {
  jsonrpc: string;
  id: string;
  result?: HeliusAssetResponse;
  error?: {
    code: number;
    message: string;
  };
}

export async function getAsset(mintAddress: string): Promise<HeliusAssetResponse> {
  await waitForRateLimit();

  const apiKey = getApiKey();
  const url = `${HELIUS_RPC_URL}/?api-key=${apiKey}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'get-asset',
      method: 'getAsset',
      params: {
        id: mintAddress,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
  }

  const data: HeliusRpcResponse = await response.json();

  if (data.error) {
    throw new Error(data.error.message || 'Failed to fetch asset');
  }

  if (!data.result) {
    throw new Error('No asset data returned');
  }

  return data.result;
}

export interface SearchAssetsResponse {
  jsonrpc: string;
  id: string;
  result?: {
    items: HeliusAssetResponse[];
    total: number;
  };
  error?: {
    code: number;
    message: string;
  };
}

export interface NFTMetadataJson {
  name?: string;
  symbol?: string;
  description?: string;
  image?: string;
  external_url?: string;
  attributes?: Array<{
    trait_type: string;
    value: string | number;
  }>;
  properties?: {
    files?: Array<{
      id?: string;
      uri: string;
      type: string;
    }>;
    category?: string;
    creators?: Array<{
      address: string;
      share: number;
    }>;
  };
}

export async function fetchMetadataFromUri(jsonUri: string): Promise<NFTMetadataJson | null> {
  try {
    const response = await fetch(jsonUri);

    if (!response.ok) {
      console.warn(`Failed to fetch metadata from ${jsonUri}: ${response.status}`);
      return null;
    }

    const metadata: NFTMetadataJson = await response.json();
    return metadata;
  } catch (error) {
    console.warn(`Error fetching metadata from ${jsonUri}:`, error);
    return null;
  }
}

/**
 * Check if an NFT name matches the target number.
 * Handles various naming formats like "NUB #42", "NUB#42", "NUB #0042", etc.
 */
function matchesNftNumber(name: string, targetNumber: number): boolean {
  // Try to extract the number from the name using various patterns
  // Pattern 1: "#123" or "# 123" (with or without space after #)
  // Pattern 2: "Name #123" or "Name#123"
  // Pattern 3: Numbers at the end like "Name 123"

  const patterns = [
    /#\s*(\d+)/,           // Matches "#123" or "# 123"
    /(\d+)\s*$/,           // Matches numbers at the end
  ];

  for (const pattern of patterns) {
    const match = name.match(pattern);
    if (match) {
      const extractedNumber = parseInt(match[1], 10);
      if (extractedNumber === targetNumber) {
        return true;
      }
    }
  }

  return false;
}

export async function searchAssetsByCollection(
  collectionAddress: string,
  nftNumber: number
): Promise<HeliusAssetResponse | null> {
  const apiKey = getApiKey();
  const url = `${HELIUS_RPC_URL}/?api-key=${apiKey}`;

  let page = 1;
  const limit = 1000;
  const maxPages = 100; // Safety limit: max 100,000 NFTs

  console.log(`Searching for NFT #${nftNumber} in collection ${collectionAddress}`);

  while (page <= maxPages) {
    await waitForRateLimit();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'search-assets',
        method: 'searchAssets',
        params: {
          grouping: ['collection', collectionAddress],
          page,
          limit,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
    }

    const data: SearchAssetsResponse = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'Failed to search assets');
    }

    if (!data.result?.items || data.result.items.length === 0) {
      // No more results to search
      console.log(`NFT #${nftNumber} not found after searching ${page - 1} pages`);
      return null;
    }

    console.log(`Searching page ${page} (${data.result.items.length} items, total: ${data.result.total})`);

    // Search for matching NFT in current page
    const found = data.result.items.find((item) =>
      matchesNftNumber(item.content.metadata.name, nftNumber)
    );

    if (found) {
      console.log(`Found NFT #${nftNumber}: ${found.content.metadata.name}`);
      return found;
    }

    // Check if we've searched all available items
    if (data.result.items.length < limit) {
      // This was the last page
      console.log(`NFT #${nftNumber} not found in collection (searched all ${data.result.total} items)`);
      return null;
    }

    page++;
  }

  console.warn(`Reached maximum page limit (${maxPages}) while searching for NFT #${nftNumber}`);
  return null;
}
